import moment from 'moment';

const livePrices = {
  namespaced: true,
  state: {
    pricesForTable: [],
    pricesUpdated: "",
    marketData: {},
    showDailyPriceModal: false,
    dailyPricesOpenedOnce: false,
    dailyPricesLastOpenedTime: "",
    storeFxSettlement: "",
    marketChange: 0,
    refreshSecondsLeft: -1,
    marketWindowCreatedTime: "",
    marketWindowCurrentTime: "",
    marketWindowExpiry: "",
    settlementPricesButtonText: "",
  },
  actions: {
    updateSellerPrices: async ({ commit, rootState, rootGetters }, payload) => {
      const response = await rootState.axios.get("/api/getLivePricing");
      const pricesUSD = payload.fuels;
      const data = {
        fxSettlement: payload.fxRates[0].value,
        fuels: response.data.payload,
        pricesUSD,
        productConfig: rootGetters["metadata/productConfig"],
      };
      commit("UPDATE_SELLER_PRICES", data);
    },
    getSellerProductPrices: async function ({ commit, rootState }, payload) {
      const { dailySettlement } = payload;
      const productPricesResponse = await rootState.axios.get("/api/pricing/products/live");
      const setterPayload = {
        dailySettlement,
        productPrices: productPricesResponse.data,
      };
      commit("SET_SELLER_PRODUCT_PRICES", setterPayload);
    },
    emptySellerPrices: ({ commit }) => {
      commit("EMPTY_SELLER_PRICES");
    },
    savePrices: ({ commit }, payload) => {
      commit("SAVE_PRICES", payload);
    },
    updateLivePrices: async ({ commit }, payload) => {
      commit("UPDATE_LIVE_PRICES", payload);
    },
    closeModal: ({ commit }) => {
      commit("CLOSE_MODAL");
    },
    openModal: ({ commit, dispatch }) => {
      commit("OPEN_MODAL");
      dispatch("setDailyPricesOpenedStatus", true);
    },
    setDailyPricesOpenedStatus: ({ commit }, payload) => {
      commit("SET_DAILY_PRICES_OPENED_STATUS", payload);
    },
    editSettlementPrices: ({ commit, dispatch }) => {
      dispatch("openModal");
    },
    updateMarketChange: ({ commit }, payload) => {
      commit("UPDATE_MARKET_CHANGE", payload);
    },
    setSecondsLeft: ({ commit }) => {
      commit("SET_SECONDS_LEFT");
    },
    updateMarketWindowTimes: ({ commit }, payload) => {
      commit("UPDATE_MARKET_WINDOW_TIMES", payload);
    },
    setSettlementPricesButtonText: ({ commit }, payload) => {
      commit("SET_SETTLEMENT_PRICES_BUTTON_TEXT", payload);
    },
  },
  getters: {
    getPricesUpdate(state) {
      return state.pricesUpdated;
    },
    getPrices(state) {
      return state.pricesForTable;
    },
    getFxSettlement(state) {
      return state.storeFxSettlement;
    },
    getMarketChange(state) {
      return state.marketChange;
    },
    marketData(state) {
      return state.marketData;
    },
    settlementPricesButtonText(state) {
      return state.settlementPricesButtonText;
    },
    getRefreshSecondsLeft(state) {
      return state.refreshSecondsLeft;
    },
  },
  mutations: {
    UPDATE_SELLER_PRICES: (state, payload) => {
      state.pricesForTable = [];
      const { fuels, pricesUSD, productConfig } = payload;
      state.storeFxSettlement = payload.fxSettlement;
      fuels.map((fuel) => {
        const elementToPush = {
          fuel_id: fuel.fuel_id,
          totalFixed: fuel.flatPrice ? Number(fuel.flatPrice.toFixed(2)) : "",
          totalLive: fuel.livePrice ? Number(fuel.livePrice.toFixed(2)) : "",
          marketChange: fuel.marketChange,
          density: fuel.density || fuel.lpt,
          duty: fuel.duty,
        };

        let fuelIdToSearchFor;
        switch (fuel.fuel_id) {
          case productConfig.GasoilId:
            fuelIdToSearchFor = productConfig.DieselId;
            break;
          case productConfig.BioDieselForGasoilId:
            fuelIdToSearchFor = productConfig.DieselId;
            break;
          default:
            fuelIdToSearchFor = fuel.fuel_id;
            break;
        }

        const findMatch = pricesUSD.find((el) => el.fuel_id === fuelIdToSearchFor);
        if (findMatch) {
          elementToPush.price = findMatch.value;
        }
        state.pricesForTable.push(elementToPush);
      });
      return state.pricesForTable;
    },
    EMPTY_SELLER_PRICES: (state) => {
      state.pricesForTable = [];
    },
    SAVE_PRICES: (state, payload) => {
      state.pricesForTable = payload.fuelPrices;
      state.storeFxSettlement = payload.fxSettlement;
    },
    UPDATE_LIVE_PRICES: (state, payload) => {
      const { prices, fx, ice } = payload;
      state.pricesUpdated = prices;
      state.marketData = { fx, ice };
    },
    CLOSE_MODAL: (state) => {
      state.showDailyPriceModal = false;
    },
    OPEN_MODAL: (state) => {
      state.showDailyPriceModal = true;
    },
    SET_DAILY_PRICES_OPENED_STATUS: (state, payload) => {
      state.dailyPricesOpenedOnce = payload;
      state.dailyPricesLastOpenedTime = new Date();
    },
    UPDATE_MARKET_CHANGE: (state, payload) => {
      state.marketChange = payload;
    },
    SET_MARKET_REFRESH_TIMER: (state, payload) => {
      state.refreshSecondsLeft = payload;
    },
    SET_SECONDS_LEFT: (state) => {
      let diff = state.refreshSecondsLeft - 1000;

      if (diff < 0) state.refreshSecondsLeft = 0;
      else state.refreshSecondsLeft = Math.abs(diff);
    },
    UPDATE_MARKET_WINDOW_TIMES: (state, payload) => {
      state.marketWindowCreatedTime = payload.created;
      state.marketWindowCurrentTime = payload.currentTime;
      state.marketWindowExpiry = payload.expires;

      const currentTime = moment(state.marketWindowCurrentTime);
      const expiry = moment(state.marketWindowExpiry);
      let diff = expiry.diff(currentTime, "milliseconds");

      if (diff < 0) state.refreshSecondsLeft = 0;
      else state.refreshSecondsLeft = Math.abs(diff);
    },
    SET_SELLER_PRODUCT_PRICES: (state, payload) => {
      const { dailySettlement, productPrices } = payload;
      state.storeFxSettlement = dailySettlement.find((rule) => rule.action.targetAttribute === "FX").action.value;

      // Map the dataset to populate the "Market Prices" table
      state.pricesForTable = productPrices.map((p) => {
        return {
          fuel_id: p.fuel_id,
          price: p.settlePriceUsd,
          totalFixed: p.settlePrice,
          totalLive: p.livePrice,
          density: p.density,
          duty: p.duty,
          bioDensity: p.bioDensity,
          bioDuty: p.bioDuty,
          bioBlend: p.bioBlend,
          usdDiff: p.usdDiff,
          marketChange: p.marketChange,
        };
      });
    },
    SET_SETTLEMENT_PRICES_BUTTON_TEXT: (state) => {
      state.settlementPricesButtonText = "Edit Settlement Prices";
    },
  },
};

export default livePrices